<template>
  <div
    class="el-select el-tag-input"
    :class="[selectSize ? 'el-select--' + selectSize : '']">
    <div
      class="el-select__tags"
      ref="tags"
      :style="{ 'max-width': inputWidth - 32 + 'px', width: '100%' }">
      <span v-if="collapseTags && value.length">
        <el-tag
          :closable="!inputDisabled"
          :size="collapseTagSize"
          type="info"
          @close="deleteTag($event, value[0])"
          disable-transitions>
          <span class="el-select__tags-text">{{ value[0] }}</span>
        </el-tag>
        <el-tag
          v-if="value.length > 1"
          :closable="false"
          :size="collapseTagSize"
          type="info"
          disable-transitions>
          <span class="el-select__tags-text">+ {{ value.length - 1 }}</span>
        </el-tag>
      </span>
      <transition-group @after-leave="resetInputHeight" v-if="!collapseTags">
        <el-tag
          v-for="val in value"
          :key="val"
          :closable="!inputDisabled"
          :size="collapseTagSize"
          type="info"
          @close="deleteTag($event, val)"
          disable-transitions>
          <span class="el-select__tags-text">{{ val }}</span>
        </el-tag>
      </transition-group>

      <input
        type="text"
        class="el-select__input"
        :class="[selectSize ? `is-${ selectSize }` : '']"
        :disabled="inputDisabled"
        @focus="handleFocus"
        @keyup="managePlaceholder"
        @keydown="resetInputState"
        @keydown.enter.prevent="addTag"
        @keydown.delete="deletePrevTag"
        v-model="query"
        :style="{ 'flex-grow': '1', width: inputLength / (inputWidth - 32) + '%', 'max-width': inputWidth - 42 + 'px' }"
        ref="input">
    </div>
    <el-input
      ref="reference"
      type="text"
      :placeholder="currentPlaceholder"
      :name="name"
      :id="id"
      :size="selectSize"
      :disabled="inputDisabled"
      :readonly="true"
      :validate-event="false"
      tabindex="-1"
      @focus="handleFocus"
      @blur="handleBlur">
      <template slot="prefix" v-if="$slots.prefix">
        <slot name="prefix"></slot>
      </template>
      <!-- <template slot="suffix" v-if="$slots.suffix">
        <slot name="suffix"></slot>
      </template> -->
      <template slot="suffix">
        <i v-show="validateState === 'validating'"
          class="el-select__caret el-input__icon el-icon-loading el-tag-input__validating"></i>
        <i v-show="validateState === 'success'"
          class="el-select__caret el-input__icon el-icon-circle-plus-outline el-tag-input__success"
          @click="addTag"></i>
        <el-tooltip ref="errorTooltip"
          v-show="validateState === 'error'"
          effect="light" placement="top">
          <i class="el-select__caret el-input__icon el-icon-warning-outline el-tag-input__error"></i>
          <template slot="content">
            <span class="danger-text">{{ validateMessage }}</span>
          </template>
        </el-tooltip>
      </template>
    </el-input>
  </div>
</template>

<script>
import AsyncValidator from 'async-validator'
import Emitter from 'element-ui/src/mixins/emitter'
import Focus from 'element-ui/src/mixins/focus'
import Locale from 'element-ui/src/mixins/locale'
import ElInput from 'element-ui/packages/input'
import ElTag from 'element-ui/packages/tag'
import Clickoutside from 'element-ui/src/utils/clickoutside'
import { addResizeListener, removeResizeListener } from 'element-ui/src/utils/resize-event'
import { t } from 'element-ui/src/locale'
import { valueEquals } from 'element-ui/src/utils/util'

export default {
  mixins: [Emitter, Locale, Focus('reference')],

  name: 'ElInputTag',

  componentName: 'ElSelect',

  inject: {
    elForm: {
      default: '',
    },

    elFormItem: {
      default: '',
    },
  },

  computed: {
    _elFormItemSize () {
      return (this.elFormItem || {}).elFormItemSize
    },

    selectSize () {
      return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size
    },

    inputDisabled () {
      return this.disabled || (this.elForm || {}).disabled
    },

    collapseTagSize () {
      return ['small', 'mini'].indexOf(this.selectSize) > -1
        ? 'mini'
        : 'small'
    },

    allRules () {
      const rules = []

      if (!this.allowDuplicate) {
        rules.push({
          validator: (rule, value, callback) => callback(
            (this.value || []).includes(value)
              ? new Error('already selected') : undefined),
        })
      }

      if (this.multipleLimit) {
        rules.push({
          validator: (rule, value, callback) => callback(
            (this.value.length || 0) >= this.multipleLimit
              ? new Error('too many selected') : undefined),
        })
      }

      if (Array.isArray(this.rules)) {
        rules.push(...this.rules)
      } else if (this.rules) {
        rules.push(this.rules)
      }

      return rules
    },
  },

  components: {
    ElInput,
    ElTag,
  },

  directives: { Clickoutside },

  props: {
    name: String,
    id: String,
    value: {
      required: true,
    },
    size: String,
    disabled: Boolean,
    multipleLimit: {
      type: Number,
      default: 0,
    },
    placeholder: {
      type: String,
      default () {
        return t('el.select.placeholder')
      },
    },
    collapseTags: Boolean,
    rules: [Array, Object],
    allowDuplicate: Boolean,
  },

  data () {
    return {
      inputLength: 20,
      inputWidth: 0,
      initialInputHeight: 0,
      cachedPlaceHolder: '',
      query: '',
      validateState: 'empty',
      validateMessage: '',
      currentPlaceholder: '',
    }
  },

  watch: {
    inputDisabled () {
      this.$nextTick(() => {
        this.resetInputHeight()
      })
    },

    placeholder (val) {
      this.cachedPlaceHolder = this.currentPlaceholder = val
    },

    value (val, oldVal) {
      this.resetInputHeight()
      if ((val && val.length) || (this.$refs.input && this.query !== '')) {
        this.currentPlaceholder = ''
      } else {
        this.currentPlaceholder = this.cachedPlaceHolder
      }
      this.query = ''
      if (!valueEquals(val, oldVal)) {
        this.dispatch('ElFormItem', 'el.form.change', val)
      }
    },

    query () {
      this.validate()
    },
  },

  methods: {
    emitChange (val) {
      if (!valueEquals(this.value, val)) {
        this.$emit('change', val)
      }
    },

    handleFocus (event) {
      this.$emit('focus', event)
    },

    blur () {
      this.$refs.reference.blur()
    },

    handleBlur (event) {
      setTimeout(() => {
        this.$emit('blur', event)
      }, 50)
    },

    deletePrevTag (e) {
      if (e.target.value.length <= 0) {
        const value = this.value.slice()
        value.pop()
        this.$emit('input', value)
        this.emitChange(value)
      }
    },

    managePlaceholder () {
      if (this.currentPlaceholder !== '') {
        this.currentPlaceholder = this.$refs.input.value ? '' : this.cachedPlaceHolder
      }
    },

    resetInputState (e) {
      this.inputLength = this.$refs.input.value.length * 15 + 20
      this.resetInputHeight()
    },

    resetInputHeight () {
      this.$nextTick(() => {
        if (!this.$refs.reference) return
        let inputChildNodes = this.$refs.reference.$el.childNodes
        let input = [].filter.call(inputChildNodes, item => item.tagName === 'INPUT')[0]
        const tags = this.$refs.tags
        const sizeInMap = this.initialInputHeight || 40
        input.style.height = !this.value.length
          ? sizeInMap + 'px'
          : Math.max(
            tags ? (tags.clientHeight + (tags.clientHeight > sizeInMap ? 6 : 0)) : 0,
            sizeInMap
          ) + 'px'
      })
    },

    addTag () {
      if (this.validateState === 'success') {
        const value = [...(this.value || []), this.query]
        this.$emit('input', value)
        this.emitChange(value)
        this.query = ''
      } else if (this.validateState === 'error' && this.$refs.errorTooltip) {
        console.log('this.$refs.errorTooltip', this.$refs.errorTooltip)
        this.$refs.errorTooltip.show()
        setTimeout(() => this.$refs.errorTooltip.hide(), 5000)
      }
    },

    deleteTag (event, tag) {
      let index = this.value.indexOf(tag)
      if (index > -1 && !this.inputDisabled) {
        const value = this.value.slice()
        value.splice(index, 1)
        this.$emit('input', value)
        this.emitChange(value)
        this.$emit('remove-tag', tag.value)
      }
      event.stopPropagation()
    },

    resetInputWidth () {
      this.inputWidth = this.$refs.reference.$el.getBoundingClientRect().width
    },

    handleResize () {
      this.resetInputWidth()
      this.resetInputHeight()
    },

    validate () {
      if (!this.query || !this.allRules.length) {
        this.validateState = this.query ? 'success' : 'empty'
        this.validateMessage = ''
        return
      }

      this.validateState = 'validating'

      const validator = new AsyncValidator({ query: this.allRules })
      validator.validate({ query: this.query }, { firstFields: true }, (errors) => {
        this.validateState = !errors ? 'success' : 'error'
        this.validateMessage = errors ? errors[0].message : ''
      })
    },
  },

  created () {
    this.cachedPlaceHolder = this.currentPlaceholder = this.placeholder
    if (!Array.isArray(this.value)) {
      this.$emit('input', [])
    }
  },

  mounted () {
    if (Array.isArray(this.value) && this.value.length > 0) {
      this.currentPlaceholder = ''
    }
    addResizeListener(this.$el, this.handleResize)

    const reference = this.$refs.reference
    if (reference && reference.$el) {
      const sizeMap = {
        medium: 36,
        small: 32,
        mini: 28,
      }
      const input = reference.$el.querySelector('input')
      this.initialInputHeight = input.getBoundingClientRect().height || sizeMap[this.selectSize]
    }
    this.$nextTick(() => {
      if (reference && reference.$el) {
        this.inputWidth = reference.$el.getBoundingClientRect().width
      }
    })
  },

  beforeDestroy () {
    if (this.$el && this.handleResize) removeResizeListener(this.$el, this.handleResize)
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  .el-tag-input {
    width: 100%;
  }
</style>
